import { generatePath, useLocation, matchPath, useParams } from 'react-router';
import qs from 'query-string';
import {
  ACCOUNT_DETAILS_PATH,
  BLOCK_DETAILS_PATH,
  BLOCK_LIST_PATH,
  CREATE_ACCOUNT_PATH,
  DELEGATE_STAKE_PATH,
  DEPLOY_DETAILS_PATH,
  DEPLOY_LIST_PATH,
  FAUCET_PATH,
  HOME_PATH,
  PEERS_PATH,
  TRANSFER_CSPR_PATH,
  UNDELEGATE_STAKE_PATH,
  VALIDATOR_DETAILS_PATH,
  VALIDATOR_LIST_PATH,
  VIEW_ADDRESS_PATH,
  RouterPathsMap,
  VALIDATOR_AUCTION_PATH,
  TOS_PATH,
  PRIVACY_POLICY_PATH,
  CHANGELOG_PATH,
  CONTRACT_PATH,
  UREF_PATH,
  CONTRACT_PACKAGE_PATH,
  SEARCH_RESULTS_PATH,
  SEARCH_PATH,
  DICTIONARY_PATH,
  CONTRACT_LIST_PATH,
  NFT_TOKEN_PATH,
  CREDITS_PATH,
  ANALYTICS_PATH,
  ACTIVITY_EXPORT_PATH,
  PURCHASE_NFT_PATH,
  ACTIVITY_EXPORT_ONBOARDING_PATH,
  ACTIVITY_EXPORT_LEARN_MORE_PATH,
  ASSOCIATED_KEYS_ONBOARDING_PATH,
  MANAGE_ASSOCIATED_KEYS_PATH,
  ASSOCIATED_KEYS_PATH,
  ASSOCIATED_KEYS_LEARN_MORE_PATH,
  DELEGATE_STAKE_SIGN_PATH,
  UNDELEGATE_STAKE_SIGN_PATH,
  TRANSFER_CSPR_SIGN_PATH,
  ACTIVITY_EXPORT_SIGN_PATH,
  PURCHASE_NFT_SIGN_PATH,
  MANAGE_ASSOCIATED_KEYS_SIGN_PATH,
  ADVERTISE_WITH_US_PATH,
  BUY_CSPR_PATH,
  routePatternsList,
  ACCOUNTS_LIST_PATH,
  TRANSACTION_LIST_PATH,
  TRANSACTION_DETAILS_PATH,
  ACCOUNT_DETAILS_OLD_PATH,
  ACCOUNT_DETAILS_TAB_PATH,
  ACCOUNT_DETAILS_SUBTAB_PATH,
  CONTRACT_FEATURED_LIST_PATH,
  CONTRACT_PACKAGE_OLD_PATH,
  CONTRACT_PACKAGE_TAB_PATH,
  CONTRACT_PACKAGE_SUBTAB_PATH,
} from './paths';
import { TFunction } from 'i18next';

export * from './navigation';

export const buildRouterPath = <Path extends keyof RouterPathsMap>(
  path: Path,
  urlParams: RouterPathsMap[Path]['urlParams'],
  queryParams: RouterPathsMap[Path]['queryParams']
) => {
  const url = generatePath(path, urlParams as any);

  return queryParams ? `${url}?${qs.stringify(queryParams)}` : url;
};

export const useRouterPath = <Path extends keyof RouterPathsMap>(
  path: Path
) => {
  const urlParams: any = useParams();
  const { search } = useLocation();

  return {
    urlParams: urlParams as RouterPathsMap[Path]['urlParams'],
    queryParams: qs.parse(search) as RouterPathsMap[Path]['queryParams'],
  };
};

export const withParams = (route, params) => {
  const matchParams = new RegExp(':([^/]+)', 'gi');
  const matchOptionalParams = new RegExp(':([^/]+)?', 'gi');

  return route
    .replace(matchParams, (matched) => {
      const param = matched.substr(
        1,
        matched.length - 1 - (matched.includes('?') ? 1 : 0)
      );
      return params[param] ? params[param] : matched;
    })
    .replace(matchOptionalParams, (matched) => '');
};

export const useRouteMatchPath = () => {
  const params = useParams();
  const { pathname } = useLocation();
  const pathPattern = routePatternsList.find((route) =>
    matchPath(route, pathname)
  );

  return {
    path: pathPattern,
    params,
  };
};

export const getRouteDetails = (
  {
    path,
    params,
  }: {
    path;
    params;
  },
  t: TFunction
): {
  name: string;
  path: string;
  params: Record<string, string>;
} => {
  switch (path) {
    case BLOCK_LIST_PATH:
      return { name: t('Blocks'), path, params };
    case BLOCK_DETAILS_PATH:
      return { name: t('Block'), path, params };
    case DEPLOY_LIST_PATH:
    case TRANSACTION_LIST_PATH:
      return {
        name: t('Transactions'),
        path,
        params,
      };
    case DEPLOY_DETAILS_PATH:
    case TRANSACTION_DETAILS_PATH:
      return {
        name: t('Transaction'),
        path,
        params,
      };
    case ACCOUNT_DETAILS_OLD_PATH:
      return { name: t('Account'), path, params };
    case ACCOUNT_DETAILS_PATH:
      return { name: t('Account'), path, params };
    case ACCOUNT_DETAILS_TAB_PATH:
      return { name: t('Account'), path, params };
    case ACCOUNT_DETAILS_SUBTAB_PATH:
      return { name: t('Account'), path, params };
    case FAUCET_PATH:
      return { name: t('Faucet'), path, params };
    case PEERS_PATH:
      return { name: t('Connected Peers'), path, params };
    case CREATE_ACCOUNT_PATH:
      return { name: t('Create an Account'), path, params };
    case VIEW_ADDRESS_PATH:
      return { name: t('View Address'), path, params };
    case VALIDATOR_DETAILS_PATH:
      return { name: t('Validator'), path, params };
    case HOME_PATH:
    case BUY_CSPR_PATH:
      return { name: t('Home'), path, params };
    case VALIDATOR_LIST_PATH:
      return { name: t('Validators'), path, params };
    case VALIDATOR_AUCTION_PATH:
      return { name: t('Validators Auction'), path, params };
    case DELEGATE_STAKE_PATH:
      return { name: t('Delegate Stake'), path, params };
    case UNDELEGATE_STAKE_PATH:
      return { name: t('Undelegate Stake'), path, params };
    case TRANSFER_CSPR_PATH:
      return { name: t('Transfer CSPR'), path, params };
    case TOS_PATH:
      return { name: t('Terms of Service'), path, params };
    case PRIVACY_POLICY_PATH:
      return { name: t('Privacy Policy'), path, params };
    case CHANGELOG_PATH:
      return { name: t('Changelog'), path, params };
    case CREDITS_PATH:
      return { name: t('Credits'), path, params };
    case SEARCH_RESULTS_PATH:
      return { name: t('Search'), path, params };
    case SEARCH_PATH:
      return { name: t('Contract'), path, params };
    case ACCOUNTS_LIST_PATH:
      return { name: t('Accounts'), path, params };
    case CONTRACT_LIST_PATH:
      return { name: t('Contracts'), path, params };
    case CONTRACT_FEATURED_LIST_PATH:
      return { name: t('Featured Contracts'), path, params };
    case CONTRACT_PATH:
      return { name: t('Contract'), path, params };
    case NFT_TOKEN_PATH:
      return { name: t('NFT'), path, params };
    case CONTRACT_PACKAGE_PATH:
      return { name: t('Contract Package'), path, params };
    case CONTRACT_PACKAGE_TAB_PATH:
      return { name: t('Contract Package'), path, params };
    case CONTRACT_PACKAGE_SUBTAB_PATH:
      return { name: t('Contract Package'), path, params };
    case CONTRACT_PACKAGE_OLD_PATH:
      return { name: t('Contract Package'), path, params };
    case UREF_PATH:
      return { name: t('URef'), path, params };
    case DICTIONARY_PATH:
      return { name: t('Dictionary'), path, params };
    case ACTIVITY_EXPORT_PATH:
      return { name: t('Account Activity Export'), path, params };
    case ACTIVITY_EXPORT_ONBOARDING_PATH:
      return { name: t('Account Activity Export'), path, params };
    case ACTIVITY_EXPORT_LEARN_MORE_PATH:
      return { name: t('How it works'), path, params };
    case PURCHASE_NFT_PATH:
      return { name: t('Purchase CSPR.live+ NFT'), path, params };
    case ANALYTICS_PATH:
      return { name: t('Analytics'), path, params };
    case ASSOCIATED_KEYS_ONBOARDING_PATH:
      return { name: t('Associated Keys'), path, params };
    case MANAGE_ASSOCIATED_KEYS_PATH:
      return { name: t('Manage Associated Keys'), path, params };
    case ASSOCIATED_KEYS_PATH:
      return { name: t('Associated Keys'), path, params };
    case ASSOCIATED_KEYS_LEARN_MORE_PATH:
      return { name: t('Learn More'), path, params };
    case DELEGATE_STAKE_SIGN_PATH:
      return { name: t('Delegate Stake'), path, params };
    case UNDELEGATE_STAKE_SIGN_PATH:
      return { name: t('Undelegate Stake'), path, params };
    case TRANSFER_CSPR_SIGN_PATH:
      return { name: t('Transfer CSPR'), path, params };
    case ACTIVITY_EXPORT_SIGN_PATH:
      return { name: t('Account Activity Export'), path, params };
    case PURCHASE_NFT_SIGN_PATH:
      return { name: t('Purchase CSPR.live+ NFT'), path, params };
    case MANAGE_ASSOCIATED_KEYS_SIGN_PATH:
      return { name: t('Manage Associated Keys'), path, params };
    case ADVERTISE_WITH_US_PATH:
      return { name: t('Advertise With Us'), path, params };
    default:
      throw Error('missing case in mapLocationMatchToText');
  }
};
